import { motion } from 'motion/react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';

import { FileCheck, Scale, Building2, Briefcase, Shield, FileText, Users, Globe, Edit3, MessageSquare, MessageCircle, Zap, ChevronRight, ChevronLeft } from 'lucide-react';

export const ExpressServicesSection = () => {
  const { ref, isVisible } = useScrollAnimation(0.1);
  const [showAllServices, setShowAllServices] = useState(false);

  const expressServices = [
    {
      icon: Building2,
      title: 'GCC/Saudi National Company Incorporation',
      turnaround: '3-5 Business Days',
      description: 'Company incorporation services for GCC nationals and Saudi citizens.'
    },
    {
      icon: FileCheck,
      title: 'Contract Drafting',
      turnaround: 'Within 24 Hours',
      description: 'Urgent contract drafting and legal risk assessment'
    },
    {
      icon: MessageSquare,
      title: 'Legal Consultation',
      turnaround: 'Same Day',
      description: 'Expert legal advice on your business matters'
    },
    {
      icon: Briefcase,
      title: 'Company Formation',
      turnaround: '5-7 Business Days',
      description: 'Complete company setup and registration services',
      tag: 'MISA License Application' 
    },
    {
      icon: Shield,
      title: 'Trademark Filing',
      turnaround: '2-3 Business Days',
      description: 'Trademark registration and IP protection'
    },
    {
      icon: Users,
      title: 'Employment Contract Drafting',
      turnaround: '1-2 Business Days',
      description: 'Compliant employment agreements for Saudi labor law'
    },
    {
      icon: FileText,
      title: 'Commercial Agreement Review',
      turnaround: '2-3 Business Days',
      description: 'Comprehensive review of commercial contracts'
    },
    // {
    //   icon: Globe,
    //   title: 'MISA License Application',
    //   turnaround: '7-10 Business Days',
    //   description: 'Foreign investment licensing and compliance'
    // },
    {
      icon: Edit3,
      title: 'Power of Attorney',
      turnaround: '1 Business Day',
      description: 'Quick POA drafting and notarization support'
    },
    {
      icon: Scale,
      title: 'Legal Opinion Letter',
      turnaround: '2-3 Business Days',
      description: 'Professional legal opinions for business decisions'
    },
  ];

  const handleWhatsAppClick = (serviceName: string) => {
    const message = encodeURIComponent(`Hi, I'm interested in your Express Service: ${serviceName}. Please provide more details.`);
    const whatsappNumber = '+966545339111';
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <section id="services" aria-label="Express Legal Services" className="relative bg-[#0a0a0a] py-12 sm:py-16 lg:py-24 overflow-hidden w-full">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(195, 165, 101, 1) 1px, transparent 0)`,
          backgroundSize: '48px 48px',
        }}></div>
      </div>

      {/* Accent Gradient */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#c3a565]/5 blur-[120px] rounded-full"></div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" ref={ref}>
        {/* Section Header */}
        <div className="mb-12 sm:mb-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-4 mb-5"
          >
            <div className="h-[1px] w-12 sm:w-16 bg-[#c3a565]"></div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#c3a565]" fill="#c3a565" />
              <span className="text-xs sm:text-sm tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#c3a565]" style={{ fontWeight: 700 }}>
               Legal Consultation & Advisory
              </span>
            </div>
            <div className="h-[1px] w-12 sm:w-16 bg-[#c3a565]"></div>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#faf9f7] text-3xl sm:text-4xl lg:text-5xl mb-4"
            style={{ fontWeight: 600 }}
          >
            Express Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-[#faf9f7]/60 max-w-2xl mx-auto text-base sm:text-lg"
            style={{ fontWeight: 300 }}
          >
            Get urgent legal support with our fast-track services. Connect instantly via WhatsApp for immediate assistance.
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
          {expressServices.slice(0, showAllServices ? expressServices.length : 8).map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group"
              >
                <div className="bg-[#1a1a1a] border border-[#c3a565]/10 rounded-lg p-6 sm:p-7 hover:border-[#c3a565]/30 hover:shadow-[0_0_30px_rgba(195,165,101,0.12)] hover:scale-[1.03] hover:bg-[#1a1a1a]/90 transition-all duration-500 h-full flex flex-col">
                  {/* Icon & Badge */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="group-hover:scale-110 transition-transform duration-500">
                      <Icon className="w-8 h-8 sm:w-7 sm:h-7 text-[#c3a565] group-hover:text-[#d4b676] transition-colors duration-500" strokeWidth={1.5} />
                    </div>
                                <button
  type="button"
  onClick={() => handleWhatsAppClick(service.title)}
  aria-label={`Contact us on WhatsApp about ${service.title}`}
  className="
    opacity-0 scale-90
    group-hover:opacity-100 group-hover:scale-100
    transition-all duration-300
    bg-[#25D366] hover:bg-[#20BA5A]
    text-white rounded-lg p-2
    cursor-pointer
  "
>
  <FaWhatsapp
    className="w-5 h-5"
    aria-hidden="true"
    focusable="false"
  />
</button>
                  </div>

            {service.tag && (
              <span className="inline-block mt-1 mb-2 text-xs bg-[#c3a565]/15 text-[#c3a565] border border-[#c3a565]/30 rounded-full px-3 py-1" style={{ fontWeight: 600, width:'max-content' }}>
                {service.tag}
              </span>
            )}
                  {/* Title */}
                  <h3 
                    className="text-[#fff] mb-2 group-hover:text-[#c3a565] transition-colors duration-500"
                    style={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: '1.3' }}
                  >
                    {service.title}
                  </h3>

                  {/* Turnaround (only time estimate, no price) */}
                  {/* <div className="mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-1 rounded-full bg-[#c3a565]"></div>
                      <p className="text-[#c3a565] text-sm" style={{ fontWeight: 500 }}>
                        {service.turnaround}
                      </p>
                    </div>
                  </div> */}

                  {/* Description */}
                  <p className="text-[#faf9f7a6] leading-relaxed mb-4 flex-grow group-hover:text-[#faf9f7]/60 transition-colors duration-500" style={{ fontWeight: 300, fontSize: '0.9rem' }}>
                    {service.description}
                  </p>
                  
                </div>
              </motion.div>
            );
          })}
        </div>
        
        {/* Show More/Less Button */}
        {expressServices.length > 8 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isVisible ? { opacity: 1 } : {}}
            className="flex justify-center mt-8"
          >
            <button
              onClick={() => setShowAllServices(!showAllServices)}
              className="px-8 py-4 border-2 border-[#c3a565]/40 rounded-xl text-[#c3a565] hover:bg-[#c3a565]/5 active:bg-[#c3a565]/10 transition-all duration-300 flex items-center justify-center gap-2 min-h-[56px] backdrop-blur-sm cursor-pointer"
              style={{ fontWeight: 600, fontSize: '1rem' }}
            >
              {showAllServices ? (
                <>
                  <span>Show Less</span>
                  <ChevronLeft className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                </>
              ) : (
                <>
                  <span>View All {expressServices.length} Express Services</span>
                  <ChevronRight className="w-5 h-5 rotate-90" strokeWidth={2.5} />
                </>
              )}
            </button>
          </motion.div>
        )}

        {/* Bottom CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-[#c3a565]/10 via-[#c3a565]/5 to-[#c3a565]/10 border border-[#c3a565]/20 rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto">
            <h3 
              className="text-[#faf9f7] text-xl sm:text-2xl mb-3"
              style={{ fontWeight: 600 }}
            >
              Need Custom Legal Support?
            </h3>
            <p className="text-[#faf9f7]/60 mb-5 max-w-2xl mx-auto" style={{ fontWeight: 300, fontSize: '0.95rem' }}>
              Don't see what you need? Contact us directly and we'll create a tailored express solution for your specific requirements.
            </p>
            <button 
              onClick={() => handleWhatsAppClick('Custom Legal Support')}
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white py-3 px-8 rounded-lg transition-all duration-300 cursor-pointer shadow-[0_4px_16px_rgba(37,211,102,0.25)] hover:shadow-[0_6px_24px_rgba(37,211,102,0.35)] hover:scale-[1.03]"
              style={{ fontWeight: 600, fontSize: '1rem' }}
            >
              <FaWhatsapp className="w-5 h-5" strokeWidth={2} />
              <span>Chat with Our Team</span>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};